import React from 'react';
import { Button } from '../../components/ui/button';
import { FileEdit, Clock, ShieldCheck } from 'lucide-react';
import { formatCATDateTime } from '../../utils/timezone';

export const STAGES = {
  AWAITING_PREAUTH: { label: 'Awaiting Pre-authorization', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  WAITING_CONSULTANT: { label: 'Waiting for Doctor', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  IN_CONSULTATION: { label: 'In Consultation', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  AWAITING_LAB_APPROVAL: { label: 'Waiting for Acacia Office Approval (Lab)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  LAB_PENDING: { label: 'Laboratory', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  CONSULTANT_REVIEW: { label: 'Results Review (Doctor)', color: 'bg-violet-100 text-violet-800 border-violet-300' },
  AWAITING_RAD_APPROVAL: { label: 'Waiting for Acacia Office Approval (Rad)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  RAD_PENDING: { label: 'In Radiology', color: 'bg-sky-100 text-sky-800 border-sky-300' },
  AWAITING_SERVICE_APPROVAL: { label: 'Waiting for Acacia Office Approval (Services)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  AWAITING_REFERRAL_APPROVAL: { label: 'Waiting for Acacia Office Approval (Referral)', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  AWAITING_DENTAL_APPROVAL: { label: 'Waiting for Acacia Office Approval (Dental)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  DENTAL_PENDING: { label: 'In Dental', color: 'bg-teal-100 text-teal-800 border-teal-300' },
  DIAGNOSTICS_PENDING: { label: 'Tests In Progress', color: 'bg-violet-100 text-violet-800 border-violet-300' },
  AWAITING_RX_APPROVAL: { label: 'Waiting for Acacia Office Approval (Rx)', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  PHARMACY_PENDING: { label: 'Pharmacy', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  READY_FOR_BILLING: { label: 'Ready for Billing', color: 'bg-green-100 text-green-800 border-green-300' },
  AWAITING_ADMISSION_APPROVAL: { label: 'Waiting for Acacia Office Approval (Admission)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  ADMITTED: { label: 'Admitted (Inpatient)', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  BILLED: { label: 'Billed', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-300' },
};

export const StageBadge = ({ status }) => {
  const s = STAGES[status] || { label: status, color: 'bg-gray-100 text-gray-700 border-gray-300' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.color}`} data-testid={`stage-badge-${status}`}>
      {s.label}
    </span>
  );
};

export const money = (visit, amount) => `${visit?.currency_symbol || '$'}${Number(amount || 0).toFixed(2)}`;

export const itemsTotal = (items) => (items || []).reduce((s, i) => s + i.cost * (i.quantity || 1), 0);

const VITAL_LABELS = { bp: 'BP', temp: 'Temp °C', pulse: 'Pulse', resp: 'Resp', spo2: 'SpO₂ %', weight: 'Weight kg' };
export const formatVitals = (v) => (v ? Object.entries(VITAL_LABELS).filter(([k]) => v[k]).map(([k, l]) => `${l}: ${v[k]}`).join(' • ') : '');

export const VITAL_FIELDS = [
  { key: 'bp', label: 'BP (mmHg)', placeholder: '120/80' },
  { key: 'temp', label: 'Temp (°C)', placeholder: '36.8' },
  { key: 'pulse', label: 'Pulse (bpm)', placeholder: '72' },
  { key: 'resp', label: 'Resp. (/min)', placeholder: '16' },
  { key: 'spo2', label: 'SpO₂ (%)', placeholder: '98' },
  { key: 'weight', label: 'Weight (kg)', placeholder: '65' },
];

// Consultation departments a patient can be sent to at check-in (order = order seen)
export const CONSULT_DEPARTMENTS = [
  { key: 'general', label: 'General Doctor', role: 'Consultant' },
  { key: 'gynaecology', label: 'Gynaecologist', role: 'Gynaecologist' },
  { key: 'specialist', label: 'Specialist', role: 'Specialist', preauthOnly: true },
];
// Specialist clinics offered only at hospitals that require pre-authorization (Dentist/Gynaecologist already have buttons)
export const SPECIALISTS = ['ENT Specialist', 'Ophthalmologist (Eye)', 'Paediatrician', 'Orthopaedic Surgeon', 'Physician (Internal Medicine)', 'General Surgeon', 'Cardiologist', 'Dermatologist (Skin)',
  'Gynaecologist', 'Dentist', 'Obstetrician', 'Psychiatrist', 'Neurologist', 'Urologist', 'Oncologist', 'Nephrologist', 'Gastroenterologist',
  'Endocrinologist', 'Pulmonologist (Chest)', 'Rheumatologist', 'Physiotherapist', 'Nutritionist/Dietician'];
// Internal referrals to these specialists go to their own department queues (not the Specialist user)
export const SPECIALIST_INTERNAL_DEPT = { Gynaecologist: 'gynaecology', Obstetrician: 'gynaecology' };
export const REFERRAL_URGENCIES = ['Routine', 'Urgent', 'Emergency'];
export const DOCTOR_ROLES = CONSULT_DEPARTMENTS.map((d) => d.role);
export const deptLabel = (key, visit) => (key === 'specialist' && visit?.specialist ? visit.specialist : key === 'dental' ? 'Dentist' : CONSULT_DEPARTMENTS.find((d) => d.key === key)?.label || 'General Doctor');
// Departments a role may act on (specialist queue is handled only by Specialist users)
export const roleDepts = (role) => CONSULT_DEPARTMENTS.filter((d) => d.role === role).map((d) => d.key);
export const deptForRole = (role) => roleDepts(role)[0] || null;
export const visitDept = (visit) => visit?.current_department || 'general';
export const roleCovers = (role, visit) => {
  const d = roleDepts(role);
  if (d.length > 0 && !d.includes(visitDept(visit))) return false;
  // A specialist with a set specialty only sees patients referred to that specialty
  const specialty = role === 'Specialist' ? localStorage.getItem('specialty') : null;
  return !specialty || !visit?.specialist || visit.specialist === specialty;
};

// "General Doctor ✓ → Gynaecologist (now) → Dentist" route line for multi-department visits
export const DeptRoute = ({ visit }) => {
  const depts = visit?.departments && visit.departments.length ? visit.departments : ['general'];
  const idx = visit?.dept_index || 0;
  const consultDone = ['PHARMACY_PENDING', 'AWAITING_RX_APPROVAL', 'READY_FOR_BILLING', 'BILLED'].includes(visit?.status);
  return (
    <p className="text-sm text-gray-700 flex items-center gap-1 flex-wrap" data-testid={`visit-departments-${visit.visit_id}`}>
      <span className="font-medium">Doctors:</span>
      {depts.map((d, i) => {
        const done = consultDone || i < idx;
        const current = !consultDone && i === idx;
        return (
          <React.Fragment key={d}>
            {i > 0 && <span className="text-gray-400">→</span>}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : current ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-500 border-gray-200'}`} data-testid={`visit-dept-${visit.visit_id}-${d}`}>
              {deptLabel(d, visit)}{done ? ' ✓' : ''}
            </span>
          </React.Fragment>
        );
      })}
    </p>
  );
};

// Highlighted notice for the doctor: items the approver removed before lab/pharmacy processing.
export const ApproverRemovals = ({ visit }) => {
  const removedLab = (visit?.lab_orders || []).filter((o) => o.status === 'removed');
  const removedRx = (visit?.prescriptions || []).filter((p) => p.status === 'removed');
  if (removedLab.length === 0 && removedRx.length === 0) return null;
  const Section = ({ label, items }) => items.length > 0 && (
    <div className="mt-1">
      <span className="font-semibold">{label}:</span>{' '}
      {items.map((i) => `${i.item_name} (x${i.quantity || 1})`).join(', ')}
      {items[0]?.removal_reason && (
        <div className="text-xs mt-0.5"><span className="font-semibold">Reason:</span> {items[0].removal_reason}</div>
      )}
    </div>
  );
  return (
    <div className="mt-3 p-3 bg-orange-50 border border-orange-300 rounded-lg text-sm text-orange-800" data-testid={`approver-removals-${visit.visit_id}`}>
      <p className="font-bold flex items-center gap-1">⚠️ Items removed by Acacia office ({removedLab.length + removedRx.length})</p>
      <Section label="Lab tests" items={removedLab} />
      <Section label="Medications" items={removedRx} />
      <p className="text-xs mt-1 text-orange-600">
        Removed by the Acacia office before lab/pharmacy processing.
      </p>
    </div>
  );
};

const formatWait = (mins) => {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

export const WaitBadge = ({ visit }) => {
  const [, tick] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, []);
  if (['BILLED', 'CANCELLED'].includes(visit.status) || !visit.checked_in_at) return null;
  const mins = Math.max(0, Math.floor((Date.now() - new Date(visit.checked_in_at).getTime()) / 60000));
  const color = mins < 30
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : mins < 120
      ? 'bg-amber-100 text-amber-800 border-amber-300'
      : 'bg-red-100 text-red-700 border-red-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`} data-testid={`wait-badge-${visit.visit_id}`}>
      <Clock className="w-3 h-3" />
      Waiting {formatWait(mins)}
    </span>
  );
};

export const VisitCard = ({ visit, showHospital, actionLabel, onAction, actionTestId, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`visit-card-${visit.visit_id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-gray-800">{visit.patient_name}</span>
          <StageBadge status={visit.status} />
          <WaitBadge visit={visit} />
          {visit.admin_edits?.length > 0 && localStorage.getItem('username') === 'superadmin' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300" title={visit.admin_edits[visit.admin_edits.length - 1].detail} data-testid={`admin-edited-badge-${visit.visit_id}`}>
              <ShieldCheck className="w-3 h-3" />
              Edited by Acacia office ({visit.admin_edits.length})
            </span>
          )}
          {visit.draft?.savedAt && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300" data-testid={`draft-badge-${visit.visit_id}`}>
              <FileEdit className="w-3 h-3" />
              Unfinished draft{visit.draft.saved_by ? ` — ${visit.draft.saved_by}` : ''}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {visit.visit_id} • Serial: {visit.patient_serial_number} • {visit.patient_sex} • DOB: {visit.patient_dob}
          {showHospital ? ` • ${visit.hospital_name}` : ''}
        </p>
        <p className="text-xs text-gray-500">Checked in {formatCATDateTime(visit.checked_in_at)} by {visit.checked_in_by}</p>
        {visit.reason && (
          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Reason:</span> {visit.reason}</p>
        )}
        {visit.specialist && (
          <p className="text-sm text-indigo-800" data-testid={`visit-specialist-${visit.visit_id}`}><span className="font-medium">Specialist referral:</span> {visit.specialist}
            {visit.referral?.approval?.status === 'pending' && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">awaiting Acacia office approval</span>}
          </p>
        )}
        {visit.referral?.approval?.status === 'rejected' && (
          <p className="text-sm text-red-700" data-testid={`visit-referral-rejected-${visit.visit_id}`}><span className="font-medium">Referral to {visit.referral.specialist} declined by Acacia office</span>{visit.referral.approval.rejection_reason ? ` — ${visit.referral.approval.rejection_reason}` : ''}</p>
        )}
        {visit.service_approval?.removed_items?.length > 0 && (
          <p className="text-sm text-orange-700" data-testid={`visit-services-removed-${visit.visit_id}`}><span className="font-medium">Services removed by Acacia office:</span> {visit.service_approval.removed_items.join(', ')}{(visit.service_approval.removal_reason || visit.service_approval.rejection_reason) ? ` — ${visit.service_approval.removal_reason || visit.service_approval.rejection_reason}` : ''}</p>
        )}
        {(visit.consultation?.doctor_name || visit.consultation?.consultant || visit.consultation_history?.length > 0) && (
          <p className="text-xs text-gray-600" data-testid={`visit-doctor-${visit.visit_id}`}><span className="font-medium">Doctor:</span> {[...(visit.consultation_history || []), ...(visit.consultation ? [visit.consultation] : [])].map((c) => c.doctor_name || c.consultant).filter((d, i, a) => d && a.indexOf(d) === i).join(', ')}</p>
        )}
        {visit.external_referral && (
          <p className="text-sm text-violet-800" data-testid={`visit-external-referral-${visit.visit_id}`}>
            <span className="font-medium">External referral:</span> {visit.external_referral.specialist} at {visit.external_referral.facility} ({visit.external_referral.urgency})
            {visit.external_referral.status === 'pending' && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">awaiting Acacia office approval</span>}
            {visit.external_referral.status === 'approved' && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-mono">{visit.external_referral.number}</span>}
            {visit.external_referral.status === 'rejected' && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-xs">rejected</span>}
          </p>
        )}
        {(visit.client_location || visit.client_phone) && (
          <p className="text-xs text-gray-600" data-testid={`visit-client-${visit.visit_id}`}>{visit.client_location && <span>Location: {visit.client_location}</span>}{visit.client_location && visit.client_phone && ' • '}{visit.client_phone && <span>Phone: {visit.client_phone}</span>}</p>
        )}
        {!['BILLED', 'CANCELLED'].includes(visit.status) && <DeptRoute visit={visit} />}
        {visit.admission?.number && (
          <p className="text-sm text-rose-800" data-testid={`visit-admission-${visit.visit_id}`}><span className="font-medium">Admission #:</span> <span className="font-mono">{visit.admission.number}</span>{visit.admission.ward ? ` • Ward: ${visit.admission.ward}` : ''}{visit.admission.status === 'discharged' ? ' • Discharged' : ''}</p>
        )}
        {visit.preauth?.number && (
          <p className="text-sm text-gray-700" data-testid={`visit-preauth-${visit.visit_id}`}><span className="font-medium">Pre-auth #:</span> <span className="font-mono text-emerald-700">{visit.preauth.number}</span></p>
        )}
        {formatVitals(visit.consultation?.vitals || visit.vitals) && (
          <p className="text-sm text-gray-700" data-testid={`visit-vitals-${visit.visit_id}`}><span className="font-medium">Vitals:</span> {formatVitals(visit.consultation?.vitals || visit.vitals)}</p>
        )}
        {visit.consultation?.complaints && (
          <p className="text-sm text-gray-700 whitespace-pre-line"><span className="font-medium">Complaints:</span> {visit.consultation.complaints}</p>
        )}
        {(visit.consultation?.provisional_diagnosis || visit.consultation?.diagnosis) && (
          <p className="text-sm text-gray-700 whitespace-pre-line"><span className="font-medium">Provisional Dx:</span> {visit.consultation.provisional_diagnosis || visit.consultation.diagnosis}</p>
        )}
        {visit.consultation?.final_diagnosis && (
          <p className="text-sm text-gray-700 whitespace-pre-line"><span className="font-medium">Final Dx:</span> {visit.consultation.final_diagnosis}</p>
        )}
      </div>
      {actionLabel && onAction && (
        <Button size="sm" onClick={onAction} data-testid={actionTestId} className="bg-blue-600 hover:bg-blue-700 flex-shrink-0">
          {actionLabel}
        </Button>
      )}
    </div>
    {children}
  </div>
);

export const ItemsTable = ({ title, items, visit, showResult = false, showInstructions = false }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>
      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 font-semibold text-gray-600">Item</th>
              <th className="text-center p-2 font-semibold text-gray-600">Qty</th>
              <th className="text-right p-2 font-semibold text-gray-600">Cost</th>
              <th className="text-right p-2 font-semibold text-gray-600">Total</th>
              {showInstructions && <th className="text-left p-2 font-semibold text-gray-600">Instructions</th>}
              {showResult && <th className="text-left p-2 font-semibold text-gray-600">Result</th>}
              <th className="text-left p-2 font-semibold text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i, idx) => (
              <tr key={i.order_id || i.rx_id || `${i.item_id}-${idx}`} className="border-t border-gray-100">
                <td className="p-2 text-gray-800">{i.item_name}</td>
                <td className="p-2 text-center">{i.quantity || 1}</td>
                <td className="p-2 text-right">{money(visit, i.cost)}</td>
                <td className="p-2 text-right">{money(visit, i.cost * (i.quantity || 1))}</td>
                {showInstructions && <td className="p-2 text-gray-600">{i.instructions || '—'}</td>}
                {showResult && <td className="p-2 text-gray-600 break-words [overflow-wrap:anywhere]">{i.result || '—'}</td>}
                <td className="p-2 capitalize text-gray-600">{(i.status || 'billable').replace(/_/g, ' ')}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold">
            <tr>
              <td className="p-2" colSpan={2}>Total</td>
              <td className="p-2 text-right" colSpan={2}>{money(visit, itemsTotal(items))}</td>
              {showInstructions && <td />}
              {showResult && <td />}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
